import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Input from '../../components/ui/Input';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';

const ResetPassword = () => {
  const navigate = useNavigate();
  const { sendPasswordReset, updatePassword } = useAuth();

  const [mode, setMode] = useState('request'); // 'request' | 'update'
  const [email, setEmail] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Detect password recovery session from Supabase hash token
    const { data: listener } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        setMode('update');
      }
      // If user is already signed in after recovery, allow update
      if (session?.access_token) {
        setMode('update');
      }
    });

    // In case the page loads with an existing session (after recovery link)
    supabase.auth.getSession().then(({ data }) => {
      if (data?.session?.access_token) {
        setMode('update');
      }
    });

    return () => {
      listener?.subscription?.unsubscribe();
    };
  }, []);

  const handleSendReset = async (e) => {
    e?.preventDefault();
    setStatus('');
    setError('');
    if (!email?.trim()) {
      setError('Please enter your email.');
      return;
    }
    setIsLoading(true);
    try {
      await sendPasswordReset(email);
      setStatus('Reset email sent. Check your inbox and spam folder, then follow the link to set a new password.');
    } catch (err) {
      setError(err?.message || 'Could not send reset email. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e?.preventDefault();
    setStatus('');
    setError('');

    if (!newPassword || newPassword?.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setIsLoading(true);
    try {
      await updatePassword(newPassword);
      setStatus('Password updated! You can now sign in with your new password.');
      setTimeout(() => navigate('/user-login', { replace: true }), 1200);
    } catch (err) {
      setError(err?.message || 'Could not update password. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-20 pb-16 px-4">
        <div className="max-w-lg mx-auto bg-card border border-border rounded-2xl shadow-lg p-8 space-y-6">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
              <Icon name="KeyRound" size={20} color="var(--color-primary)" />
            </div>
            <div>
              <h1 className="text-2xl font-heading font-semibold text-foreground">
                {mode === 'request' ? 'Forgot Password' : 'Set New Password'}
              </h1>
              <p className="text-sm text-muted-foreground">
                {mode === 'request'
                  ? 'We will email you a secure link to reset your password.'
                  : 'Enter your new password below.'}
              </p>
            </div>
          </div>

          {error && (
            <div className="p-3 rounded-md border border-error bg-error/10 text-sm text-error">
              {error}
            </div>
          )}

          {status && (
            <div className="p-3 rounded-md border border-success bg-success/10 text-sm text-success">
              {status}
            </div>
          )}

          {mode === 'request' ? (
            <form className="space-y-4" onSubmit={handleSendReset}>
              <Input
                label="Email Address"
                type="email"
                name="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e?.target?.value)}
                required
              />
              <Button
                type="submit"
                variant="default"
                size="lg"
                fullWidth
                loading={isLoading}
                iconName="Mail"
                iconPosition="right"
              >
                {isLoading ? 'Sending...' : 'Send Reset Link'}
              </Button>
              <Button
                type="button"
                variant="outline"
                fullWidth
                onClick={() => navigate('/user-login')}
              >
                Back to Login
              </Button>
            </form>
          ) : (
            <form className="space-y-4" onSubmit={handleUpdatePassword}>
              <Input
                label="New Password"
                type="password"
                name="newPassword"
                placeholder="Enter new password"
                value={newPassword}
                onChange={(e) => setNewPassword(e?.target?.value)}
                required
              />
              <Input
                label="Confirm New Password"
                type="password"
                name="confirmPassword"
                placeholder="Re-enter new password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e?.target?.value)}
                required
              />
              <Button
                type="submit"
                variant="default"
                size="lg"
                fullWidth
                loading={isLoading}
                iconName="Save"
                iconPosition="right"
              >
                {isLoading ? 'Updating...' : 'Update Password'}
              </Button>
              <Button
                type="button"
                variant="outline"
                fullWidth
                onClick={() => navigate('/user-login')}
              >
                Back to Login
              </Button>
            </form>
          )}

          {mode === 'request' && (
            <p className="text-xs text-muted-foreground text-center">
              The reset link will redirect you back here to set a new password.
            </p>
          )}
        </div>
      </main>
    </div>
  );
};

export default ResetPassword;
